import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { ChartType } from 'app/models/charts.model';
import { RoundToKPipe } from 'app/modules/dashboard/pipes/round-to-k.pipe';
import { Chart, ChartData, ChartOptions, Plugin } from 'chart.js';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ChartHoverService } from '../../services/chart-hover.service';
import { ChronoPipe } from 'app/modules/dashboard/pipes/chrono.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgIf, AsyncPipe, TitleCasePipe } from '@angular/common';

@Component({
  selector: 'tl-chart-component',
  template: `
    <div [ngClass]="classes">
      <div id="chart-header">
        <div class="title">{{ title | titlecase }}</div>
        <div class="data">{{ progress | async }} {{ metrics | titlecase }}</div>
      </div>

      <ng-container *ngIf="!(dataError | async); else dataErrorTemplate">
        <div id="chart-container">
          <canvas [id]="title + 'ChartRef'">{{ chart }}</canvas>
        </div>
      </ng-container>

      <ng-template #dataErrorTemplate>
        <div class="data-error-template">
          {{ 'training_log.charts.data_error' | translate }}
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: ['./tl-chart.component.scss'],
  standalone: true,
  imports: [NgClass, NgIf, AsyncPipe, TitleCasePipe, TranslateModule],
})
export class TrainingLogChartComponent implements AfterViewInit, OnDestroy {
  chart: any;
  @Input() chartType!: ChartType;
  @Input() title!: string;
  @Input() metrics!: string;
  @Input() label!: string[];
  @Input() data!: number[];
  @Input() chartColor: string = '#428ff3';
  @Input() tension: number = 0.2;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  dataError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  progress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  lastActivePoint: any;

  // Pipes 🤿
  roundToKPipe: RoundToKPipe = new RoundToKPipe();
  chronoPipe: ChronoPipe = new ChronoPipe();

  private subscriptions: Subscription[] = [];

  constructor(private chartHoverService: ChartHoverService) {}

  public get classes(): string[] {
    return [`chart-${this.size}`];
  }

  convertColorHexWithOpacityToOpaque(hexAvecOpacite: string) {
    let r = parseInt(hexAvecOpacite.substring(1, 3), 16);
    let g = parseInt(hexAvecOpacite.substring(3, 5), 16);
    let b = parseInt(hexAvecOpacite.substring(5, 7), 16);
    let a = parseInt(hexAvecOpacite.substring(7, 9), 16) / 255;

    let fondR = 255;
    let fondG = 255;
    let fondB = 255;

    let couleurSansOpaciteR = Math.round((1 - a) * fondR + a * r);
    let couleurSansOpaciteG = Math.round((1 - a) * fondG + a * g);
    let couleurSansOpaciteB = Math.round((1 - a) * fondB + a * b);

    let hexCouleurSansOpacite =
      '#' +
      (
        (1 << 24) +
        (couleurSansOpaciteR << 16) +
        (couleurSansOpaciteG << 8) +
        couleurSansOpaciteB
      )
        .toString(16)
        .slice(1);
    return hexCouleurSansOpacite;
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }

  ngAfterViewInit(): void {
    if (this.data.length === 0 || this.label.length === 0) {
      this.dataError.next(true);
      return;
    }

    let chartdata: ChartData<any> = {
      labels: this.label,
      datasets: [{ data: this.data.map(this.roundToTwoDecimals) }],
    };

    let options: ChartOptions<any> = {
      defaultFontFamily: (Chart.defaults.font.family = "'Sk-Modernist-Bold'"),
      defaultFontSize: (Chart.defaults.font.size = 14),

      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          xAlign: 'center',
          yAlign: 'bottom',
          backgroundColor: this.convertColorHexWithOpacityToOpaque(
            this.chartColor + 40
          ),
          caretSize: 6,
          bodyColor: '#7e8184',
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          border: {
            color: '#f4f4f4',
            width: 3,
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
          },
        },
        y: {
          border: {
            display: false,
          },
          grid: {
            tickWidth: 0,
          },
          ticks: {
            crossAlign: 'center',
            padding: 8,
          },
        },
      },
    };

    let plugins: any[] = [];

    const drawVerticalLine: Plugin = {
      id: 'drawVerticalLine',
      beforeDraw: (data: any, args: any, plugins: any) => {
        const ctx = data.ctx;
        if (data._active && data._active.length > 0) {
          ctx.save();
          const activePoint = data.tooltip._active[0];
          if (activePoint) {
            ctx.beginPath();
            ctx.moveTo(activePoint.element.x, data.chartArea.top);
            ctx.lineTo(activePoint.element.x, activePoint.element.y);
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#c8c8c8';
            ctx.stroke();

            ctx.moveTo(activePoint.element.x, activePoint.element.y);
            ctx.lineTo(activePoint.element.x, data.chartArea.bottom);
            ctx.lineWidth = 2;
            ctx.strokeStyle = this.chartColor + 50;
            ctx.stroke();
          }
          ctx.restore();
        }
      },
    };

    const hideFirstTick = {
      id: 'hideFirstTick',
      beforeFit: function (axis: any) {
        var l = axis.getLabels();
        axis.ticks.push({ value: axis.max, label: l[axis.max] });
      },
    };

    const yMaxValue: number =
      Math.max(...this.data) > 100
        ? Math.round(Math.max(...this.data) / 100) * 100 + 100
        : Math.max(...this.data) > 50
          ? Math.round(Math.max(...this.data) / 10) * 10 + 10
          : Math.max(...this.data) + 1;

    switch (this.chartType) {
      case ChartType.BAR:
        chartdata = {
          ...chartdata,
          datasets: [
            {
              ...chartdata.datasets[0],
              backgroundColor: this.chartColor,
              borderRadius: 5,
            },
          ],
        };

        options = {
          ...options,
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              caretPadding: 4,
              displayColors: false,
              callbacks: {
                label: (data: any) => {
                  const roundedValue = this.roundToTwoDecimals(data.raw);
                  this.progress.next(roundedValue);
                  return roundedValue + this.metrics;
                },
                title: () => {
                  return '';
                },
              },
            },
          },
          scales: {
            x: {
              ...options.scales.x,
              ticks: {
                ...options.scales.x.ticks,
              },
            },
            y: {
              max: this.roundToTwoDecimals(
                Math.max(...this.data) > 100
                  ? Math.round(Math.max(...this.data) / 100) * 100 + 100
                  : Math.max(...this.data) > 50
                    ? Math.round(Math.max(...this.data) / 10) * 10 + 10
                    : Math.max(...this.data) + 1
              ),
              ...options.scales.y,

              ticks: {
                ...options.scales.y.ticks,
                callback: (value: any) => `${this.roundToTwoDecimals(value)}`,
              },

              grid: {
                ...options.scales.y.grid,
              },
            },
          },
        };

        plugins = [];

        break;

      case ChartType.LINE:
        chartdata = {
          ...chartdata,
          datasets: [
            {
              ...chartdata.datasets[0],
              borderColor: this.chartColor,
              backgroundColor: this.chartColor + 35,
              tension: this.tension,
              fill: true,
            },
          ],
        };

        options = {
          ...options,
          interaction: {
            intersect: false,
          },
          plugins: {
            ...options.plugins,
            tooltip: {
              ...options.plugins.tooltip,
              mode: 'index',
              displayColors: false,
              callbacks: {
                label: (data: any) => {
                  const roundedValue = this.roundToTwoDecimals(data.raw);
                  this.progress.next(roundedValue);
                  return roundedValue + this.metrics;
                },
                title: () => {
                  return '';
                },
              },
            },
          },
          scales: {
            x: {
              ...options.scales.x,
              ticks: {
                ...options.scales.x.ticks,
                autoSkipPadding: 30,
                sampleSize: 20,
                align: 'end',
                callback: (data: any) => {
                  const label: number = Number.parseInt(this.label[data]);
                  return data > 0 ? this.chronoPipe.transform(label) : '';
                },
              },
            },
            y: {
              stack: 'stack',
              max: yMaxValue,
              ...options.scales.y,
              ticks: {
                ...options.scales.y.ticks,
                callback: (value: any) => `${this.roundToTwoDecimals(value)}`,
              },
            },
          },
          elements: {
            point: {
              radius: 0,
            },
          },
        };

        plugins = [...plugins, drawVerticalLine, hideFirstTick];
        break;
    }

    this.chart = new Chart(`${this.title}ChartRef`, {
      type: this.chartType,
      data: chartdata,
      options: options,
      plugins: plugins,
    });

    this.chart.options.onHover = (event: any, elements: any[]) => {
      if (elements.length) {
        const element = elements[0];
        const label = this.chart.data.labels[element.index];
        this.chartHoverService.setHoverLabel(label); // Emit hover label
      } else {
        this.chartHoverService.setHoverLabel(null); // Emit null when not hovering
      }
    };

    // Subscribe to hover label changes
    this.subscriptions.push(
      this.chartHoverService.hoverLabel$.subscribe((label) => {
        if (label && this.chart) {
          const index = this.chart.data.labels.indexOf(label);
          const meta = this.chart.getDatasetMeta(0);
          const points = meta.data;
          const activePoint = points[index];

          if (activePoint) {
            this.chart.tooltip.setActiveElements(
              [
                {
                  datasetIndex: 0,
                  index: index,
                },
              ],
              { x: activePoint.x, y: activePoint.y }
            );
            this.chart.update('none'); // Use 'none' to avoid animation
            this.chart.render(); // Force render to trigger plugins
          }
        } else {
          this.chart.tooltip.setActiveElements([], { x: 0, y: 0 });
          this.chart.update('none');
          this.chart.render();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
